import React, { useEffect, useState } from "react";
import { SOCIAL_LINKS } from "../../Constant/competition";
import { FOOTER_LEGAL, STORE_LINKS } from "../../Constant/footer";
import { useLanguage } from "../../context/LanguageContext";
import qrCode from "../../assets/images/QR-Code.svg";
import appQr from "../../assets/images/App-QR.svg";
import androidBadge from "../../assets/images/android.svg";
import appStoreBadge from "../../assets/images/appstore.svg";

const renderBody = (item) => {
  if (!item.link) return item.body;

  const { text, href } = item.link;
  const parts = item.body.split(text);

  return parts.reduce((nodes, part, index) => {
    nodes.push(part);
    if (index < parts.length - 1) {
      nodes.push(
        <a key={`${item.id}-link-${index}`} href={href} target="_blank" rel="noreferrer">
          {text}
        </a>
      );
    }
    return nodes;
  }, []);
};

const Footer = () => {
  const { t } = useLanguage();
  const [qrOpen, setQrOpen] = useState(false);
  const storeBadges = [
    { id: "qr", src: qrCode, alt: t("footer.qrAlt"), className: "site-footer__qr", isQr: true },
    {
      id: "android",
      src: androidBadge,
      alt: t("footer.playStore"),
      className: "site-footer__store-badge",
      href: STORE_LINKS.playStore,
    },
    {
      id: "appstore",
      src: appStoreBadge,
      alt: t("footer.appStore"),
      className: "site-footer__store-badge",
      href: STORE_LINKS.appStore,
    },
  ];
  const legal = [
    {
      ...FOOTER_LEGAL[0],
      label: t("footer.regulationLabel"),
      body: t("footer.regulationBody"),
    },
    {
      ...FOOTER_LEGAL[1],
      label: t("footer.introductionLabel"),
      body: t("footer.introductionBody"),
    },
  ];

  useEffect(() => {
    if (!qrOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setQrOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [qrOpen]);

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__top">
          <div className="site-footer__stores">
            {storeBadges.map((badge) => {
              if (badge.isQr) {
                return (
                  <button
                    key={badge.id}
                    type="button"
                    className="site-footer__qr-btn"
                    aria-label={t("footer.showQr")}
                    onClick={() => setQrOpen(true)}
                  >
                    <img className={badge.className} src={badge.src} alt={badge.alt} />
                  </button>
                );
              }

              return badge.href ? (
                <a
                  key={badge.id}
                  href={badge.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={badge.alt}
                >
                  <img className={badge.className} src={badge.src} alt={badge.alt} />
                </a>
              ) : (
                <img
                  key={badge.id}
                  className={badge.className}
                  src={badge.src}
                  alt={badge.alt}
                />
              );
            })}
          </div>

          <nav className="site-footer__social" aria-label={t("footer.social")}>
            {SOCIAL_LINKS.map((link) => (
              <a
                key={link.id}
                href={link.href}
                aria-label={link.label}
                {...(link.href !== "#"
                  ? { target: "_blank", rel: "noreferrer" }
                  : {})}
              >
                {link.ring ? (
                  <span className="site-footer__social-ring">
                    <img
                      className="site-footer__social-ring-bg"
                      src={link.ring}
                      alt=""
                      aria-hidden="true"
                    />
                    <img
                      className="site-footer__social-ring-icon"
                      src={link.icon}
                      alt=""
                    />
                  </span>
                ) : (
                  <img className="site-footer__social-icon" src={link.icon} alt="" />
                )}
              </a>
            ))}
          </nav>
        </div>

        <div className="site-footer__legal">
          {legal.map((item) => (
            <p key={item.id} className="site-footer__disclaimer">
              <span className="site-footer__disclaimer-label">{item.label}</span>{" "}
              {renderBody(item)}
            </p>
          ))}
        </div>

        <div className="site-footer__bottom">
          <div className="site-footer__divider" aria-hidden="true" />
          <div className="site-footer__copy">{t("footer.copyright")}</div>
        </div>
      </div>

      {qrOpen ? (
        <div
          className="site-footer__qr-modal"
          role="dialog"
          aria-modal="true"
          aria-label={t("footer.scanQr")}
          onClick={() => setQrOpen(false)}
        >
          <div
            className="site-footer__qr-dialog"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              className="site-footer__qr-popup-img"
              src={appQr}
              alt={t("footer.scanQr")}
            />
          </div>
        </div>
      ) : null}
    </footer>
  );
};

export default Footer;
